import React from "react"

import {
	AppBar,
	Avatar,
	Badge,
	Box,
	Button,
	CssBaseline,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem, Popover,
	Stack,
	Toolbar,
	Typography
} from "@mui/material"
import { AccountCircle, Notifications, Message } from "@mui/icons-material"
import { styled } from "@mui/material/styles"

import Icon from "@mdi/react"
import {
	mdiAccountMultiple,
	mdiAccountTieHat, mdiCalendarMonth,
	mdiAlert,
	mdiBell,
	mdiCloseThick, mdiHeartPlusOutline,
	mdiCog,
	mdiHomeCity,
	mdiHomeSwitch,
	mdiMenu, mdiPencilOutline,
	mdiStore
} from "@mdi/js"

import { Action, ActionStatus, ActionsRequest, PaginatedResponse, Profile, SessionResponse, UpdateProfileDetails, ConversationSummary } from "models"
import withUseRouter, { UseRouterParams } from "services/router"
import { StorageEntries } from "services/storage-entries"
import { AuthenticationService } from "services/authentication.service"
import SidebarProfileInfoForm from "components/sidebar/sidebarProfileInfoForm"
import { Sidebar } from "../sidebar/sidebar"
import { theme } from "index"
import { MatesComponent, MatesComponentState } from "../../components/mates-component"
import { ProfileServices } from "../../services/profile.services"
import { ErrorResponse, RestResponse } from "@sinossi/mates-react-library"
import { ActionsServices } from "services/actions.services"
import { MessageService } from "services/message.service"
import { Trans } from "@lingui/react"
import { Link } from "react-router-dom"
import NotificationMenu from "./notification-menu"
import WelcomeModal from "./welcome-modal"
import { NotificationItemData } from "./notification-list-item"

const CustomIconButton = styled(IconButton)(({ theme }) => ({
	color: "text",
	"&:hover": {
		color: theme.palette.secondary.main,
		backgroundColor: "unset",
	},
}))
const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
	paddingTop: "2px",
	paddingBottom: "2px",

	"&:hover": {
		color: theme.palette.secondary.main,
		backgroundColor: "unset",
	},
	"&:hover div.MuiListItemIcon-root": {
		color: theme.palette.secondary.main,
	},
}))
const CustomListItemText = styled(ListItemText)(() => ({
	"& .MuiTypography-root": {
		fontSize: "0.7rem",
		fontWeight: "500",
	},
}))
const CustomListItemIcon = styled(ListItemIcon)(() => ({
	"&": {
		minWidth: "unset !important",
		marginRight: "12px",
	},
}))

const NotificationContainer = styled(Box)(({ theme }) => ({
	minWidth: "320px",
	maxWidth: "400px",
}))

const NotificationHeader = styled(Typography)(({ theme }) => ({
	padding: theme.spacing(2, 2, 1, 2),
	fontSize: "1rem",
	fontWeight: 600,
	color: theme.palette.text.primary,
	borderBottom: `1px solid ${theme.palette.divider}`,
	marginBottom: theme.spacing(1),
}))

const NotificationList = styled(List)(({ theme }) => ({
	width: "100%",
	maxHeight: "60vh",
	overflow: "auto",
	padding: 0,
	"& .MuiListItemButton-root": {
		borderBottom: `1px solid ${theme.palette.divider}`,
		"&:last-child": {
			borderBottom: "none",
		},
		"&:hover": {
			backgroundColor: theme.palette.action.hover,
		},
	},
}))

const EmptyNotificationContainer = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	padding: theme.spacing(4, 2),
	minHeight: "200px",
	color: theme.palette.text.secondary,
}))

const ViewAllButtonContainer = styled(Box)(({ theme }) => ({
	padding: theme.spacing(1, 2, 2, 2),
}))

interface HeaderProps extends UseRouterParams {
	sessionData: SessionResponse
}
interface HeaderState extends MatesComponentState {
	showUserMenu: boolean
	showSettingsMenu: boolean
	profileDetailOpen: boolean
	profileDetails: Profile | null
	showNotificationMenu: boolean
	notificationActions: PaginatedResponse<Action> | null
	showWelcomeModal: boolean
	hasCheckedWelcomeModal: boolean
	selectedAction: Action | null
	showActionDialog: boolean
	showMessageMenu: boolean
	conversationsSummary: ConversationSummary[] | null
	unreadMessagesCount: number
	isUpdatingMessages: boolean
}

class Header extends React.Component<HeaderProps, HeaderState> {
	private readonly authenticationService: AuthenticationService = AuthenticationService.init()

	private readonly drawerWidth = 260

	private readonly userMenuRef: React.RefObject<HTMLButtonElement> = React.createRef()
	private readonly notificationMenuRef: React.RefObject<HTMLButtonElement> = React.createRef()
	private readonly notificationListRef: React.RefObject<any> = React.createRef()
	private readonly messageMenuRef: React.RefObject<HTMLButtonElement> = React.createRef()
	private readonly messageListRef: React.RefObject<any> = React.createRef()

	private readonly settingsMenuRef: React.RefObject<HTMLButtonElement> = React.createRef()

	private readonly sidebarRef: React.RefObject<Sidebar> = React.createRef()

	private readonly matesComponent: MatesComponent<HeaderProps, HeaderState> = new MatesComponent(this)

	private notificationRequest: ActionsRequest = ActionsRequest.empty()
	
	// Polling per aggiornamento messaggi
	private messagePollingInterval: NodeJS.Timeout | null = null
	private readonly POLLING_INTERVAL_MS = 20000 // 20 secondi

	constructor(props: HeaderProps) {
		super(props)
		this.state = {
			...this.matesComponent.setupState(),
			showUserMenu: false,
			showSettingsMenu: false,
			profileDetailOpen: false,
			profileDetails: null,
			showNotificationMenu: false,
			notificationActions: null,
			showWelcomeModal: false,
			hasCheckedWelcomeModal: false,
			selectedAction: null,
			showActionDialog: false,
			showMessageMenu: false,
			conversationsSummary: null,
			unreadMessagesCount: 0,
			isUpdatingMessages: false,
		}
	}

	componentDidMount(): void {
		this.getNotificationActions()
		this.getConversationsSummary()
		this.getUnreadMessagesCount()
		this.startMessagePolling()
	}

	componentWillUnmount(): void {
		this.stopMessagePolling()
	}

	componentDidUpdate(prevProps: any, prevState: any) {
		if (!prevState.showNotificationMenu && this.state.showNotificationMenu) {
			if (this.notificationListRef.current) {
				this.notificationListRef.current.scrollTop = 0;
			}
		}
	}

	private openUserMenu = (): void => {
		this.setState(() => ({
			showUserMenu: true,
			showSettingsMenu: false,
			profileDetailOpen: false,
		}), this.getProfileData)
	}
	private closeUserMenu = (): void => {
		this.setState(() => ({ showUserMenu: false }))
	}

	private getProfileData = () => {
		ProfileServices.getProfile(this.getProfileDataCallback, this.matesComponent.onException)
	}

	private getProfileDataCallback = (response: RestResponse<Profile, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		this.setState(
			() => ({ profileDetails: response.data, })
		)
	}

	private openSettingsMenu = (): void => {
		this.setState(() => ({
			showSettingsMenu: true,
			showUserMenu: false,
			profileDetailOpen: false,
		}))
	}
	private closeSettingsMenu = (): void => {
		this.setState(() => ({ showSettingsMenu: false }))
	}

	private handleDrawerToggle = (): void => {
		this.sidebarRef.current?.handleDrawerToggle()
	}

	private openProfileDetail = (): void => {
		this.setState(() => ({
			profileDetailOpen: true,
			showUserMenu: false,
			showSettingsMenu: false,
		}))
	}
	private closeProfileDetail = (): void => {
		this.setState(() => ({ profileDetailOpen: false }))
	}

	private logout = (): void => {
		this.closeUserMenu()

		this.authenticationService.logout()
	}

	private submitForm = (request: UpdateProfileDetails) => {
		ProfileServices.putProfile(request, this.putProfileCallback, this.matesComponent.onException)
	}

	private putProfileCallback = () => {
		this.getProfileData()
	}

	private getNotificationActions = (): void => {
		const request = {
			...this.notificationRequest,
			pageId: 0
		}

		this.setState(() => ({
			//loading: true
		}), () => ActionsServices.getNotificationActions(
			request,
			this.getNotificationActionsCallback,
			this.matesComponent.onException
		))
	}

	private getNotificationActionsCallback = (response: RestResponse<PaginatedResponse<Action>, ErrorResponse>): void => {
		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		const notificationItems = response.data?.items ?? []
		const unreadCount = notificationItems.filter((notification) => !this.isNotificationRead(notification)).length

		this.setState((prevState) => {
			const isFirstCheck = !prevState.hasCheckedWelcomeModal
			const shouldOpenWelcomeModal = isFirstCheck && (unreadCount > 0 || prevState.unreadMessagesCount > 0)

			return {
				//loading: false,
				notificationActions: response.data,
				showWelcomeModal: shouldOpenWelcomeModal ? true : prevState.showWelcomeModal,
				hasCheckedWelcomeModal: isFirstCheck ? true : prevState.hasCheckedWelcomeModal,
			}
		})
	}

	private markNotificationAsRead = (notificationId: string): void => {
		ActionsServices.markNotificationAsRead(
			notificationId,
			this.markNotificationAsReadCallback,
			() => {
				// Non bloccare l'apertura del dettaglio in caso di errore.
			},
		)
	}

	private markNotificationAsReadCallback = (): void => {
		this.getNotificationActions()
	}

	private toggleNotificationMenu = () => {
		this.setState((prev) => ({
			showNotificationMenu: !prev.showNotificationMenu
		}))
	}

	private getNotificationType = (notification: Action): string => {
		const rawType =
			(notification as any)?.type ??
			(notification as any)?.notificationType ??
			null

		if (typeof rawType !== "string") {
			return "GENERIC"
		}

		const normalizedType = rawType.toUpperCase()
		if (
			normalizedType === "SYSTEM" ||
			normalizedType === "PROPOSAL" ||
			normalizedType === "MATCHING" ||
			normalizedType === "ALERT" ||
			normalizedType === "ASSET" ||
			normalizedType === "GENERIC"
		) {
			return normalizedType
		}

		return "GENERIC"
	}

	private getNotificationIcon = (notification: Action): string => {
		switch (this.getNotificationType(notification)) {
			case "SYSTEM":
				return mdiCog
			case "PROPOSAL":
				return mdiStore
			case "MATCHING":
				return mdiHomeSwitch
			case "ALERT":
				return mdiAlert
			case "ASSET":
				return mdiHomeCity
			case "GENERIC":
			default:
				return mdiBell
		}
	}

	private isNotificationRead = (notification: Action): boolean => {
		const notificationData = notification as Action & {
			isRead?: boolean
			read?: boolean
			unread?: boolean
			readAt?: string | null
			readDate?: string | null
		}

		if (typeof notificationData.isRead === "boolean") {
			return notificationData.isRead
		}
		if (typeof notificationData.read === "boolean") {
			return notificationData.read
		}
		if (typeof notificationData.unread === "boolean") {
			return !notificationData.unread
		}
		if (notificationData.readAt || notificationData.readDate) {
			return true
		}

		return notification.status === ActionStatus.DONE || notification.status === ActionStatus.SKIPPED
	}

	private getNotificationTimestamp = (notification: Action): number => {
		const dateCandidates = [
			(notification as any)?.readDate,
			(notification as any)?.readAt,
			notification.creationDate,
			notification.executionDate,
			notification.deadline,
		]

		for (const dateCandidate of dateCandidates) {
			if (!dateCandidate) {
				continue
			}
			const parsedDate = new Date(dateCandidate)
			if (!Number.isNaN(parsedDate.getTime())) {
				return parsedDate.getTime()
			}
		}

		return 0
	}

	private formatNotificationDate = (notification: Action): string => {
		const timestamp = this.getNotificationTimestamp(notification)
		if (!timestamp) {
			return "-"
		}

		return new Date(timestamp).toLocaleString("it-IT", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
		})
	}

	private getNotificationSubtitle = (notification: Action): string => {
		const accountDetails = notification.creator?.accountDetails
		if (accountDetails?.firstName || accountDetails?.lastName) {
			return `${accountDetails.firstName ?? ""} ${accountDetails.lastName ?? ""}`.trim()
		}

		if (notification.creator?.username) {
			return notification.creator.username
		}

		return ""
	}

	private getSelectedActionContentText = (): string => {
		const selectedAction = this.state.selectedAction as (Action & { content?: unknown }) | null
		const rawContent = selectedAction?.content

		return typeof rawContent === "string" ? rawContent : ""
	}

	private getSelectedActionAssetId = (): string | null => {
		const selectedAction = this.state.selectedAction as (Action & { metadata?: unknown }) | null
		const assetId = (selectedAction?.metadata as { assetId?: unknown } | undefined)?.assetId

		if (assetId === null || assetId === undefined) {
			return null
		}

		if (typeof assetId === "string" || typeof assetId === "number") {
			return String(assetId)
		}

		return null
	}

	private getSelectedActionMatchId = (): string | null => {
		const selectedAction = this.state.selectedAction as (Action & { metadata?: unknown }) | null
		const matchId = (selectedAction?.metadata as { matchId?: unknown } | undefined)?.matchId

		if (matchId === null || matchId === undefined) {
			return null
		}

		if (typeof matchId === "string" || typeof matchId === "number") {
			return String(matchId)
		}

		return null
	}

	private buildNotificationItemData = (notification: Action): NotificationItemData => {
		return {
			notification,
			title: notification.name,
			subtitle: this.getNotificationSubtitle(notification),
			dateLabel: this.formatNotificationDate(notification),
			iconPath: this.getNotificationIcon(notification),
			isRead: this.isNotificationRead(notification),
		}
	}

	private openDetails = (notification: Action): void => {
		this.markNotificationAsRead(notification.id)

		this.setState({
			selectedAction: notification,
			showActionDialog: true,
		});
	}

	private closeActionDialog = (): void => {
		this.setState({
			showActionDialog: false,
			selectedAction: null
		});
	}

	private closeWelcomeModal = (): void => {
		this.setState({
			showWelcomeModal: false,
		})
	}

	// Metodi per la gestione dei messaggi
	private getConversationsSummary = (): void => {
		MessageService.getConversationsSummary(
			5,
			this.getConversationsSummaryCallback,
			(error: any) => {
				// Reset del flag anche in caso di errore
				this.setState({ isUpdatingMessages: false });
				this.matesComponent.onException(error);
			}
		);
	}

	private getConversationsSummaryCallback = (conversations: ConversationSummary[]): void => {
		// Verifica che conversations sia un array valido
		const validConversations = Array.isArray(conversations) ? conversations : [];
		
		// Sanitizza ogni conversazione per assicurarsi che i dati siano validi per il rendering
		const sanitizedConversations = validConversations.map(conv => ({
			...conv,
			subject: typeof conv.subject === 'string' ? conv.subject : 'Conversazione senza titolo',
			lastMessage: typeof conv.lastMessage === 'string' ? conv.lastMessage : '',
			participants: Array.isArray(conv.participants) ? conv.participants : [],
			unreadCount: typeof conv.unreadCount === 'number' ? conv.unreadCount : 0
		}));
		
		this.setState(() => ({
			conversationsSummary: sanitizedConversations,
			isUpdatingMessages: false, // Reset del flag di aggiornamento
		}));
	}

	private getUnreadMessagesCount = (): void => {
		MessageService.getUnreadMessagesCount(
			this.getUnreadMessagesCountCallback,
			(error: any) => {
				// Reset del flag anche in caso di errore
				this.setState({ isUpdatingMessages: false });
				this.matesComponent.onException(error);
			}
		);
	}

	private getUnreadMessagesCountCallback = (count: number): void => {
		// Verifica che count sia un numero valido
		const validCount = typeof count === 'number' ? count : 0;
		this.setState((prevState) => {
			const isFirstCheck = !prevState.hasCheckedWelcomeModal
			const notificationItems = prevState.notificationActions?.items ?? []
			const notificationsUnread = notificationItems.filter((notification) => !this.isNotificationRead(notification)).length
			const shouldOpenWelcomeModal = isFirstCheck && (notificationsUnread > 0 || validCount > 0)

			return {
				unreadMessagesCount: validCount,
				isUpdatingMessages: false,
				showWelcomeModal: shouldOpenWelcomeModal ? true : prevState.showWelcomeModal,
				hasCheckedWelcomeModal: shouldOpenWelcomeModal ? true : prevState.hasCheckedWelcomeModal,
			}
		});
	}

	private isCurrentUser = (senderId: string): boolean => {
		return senderId === this.props.sessionData?.accountDetails?.account?.id;
	};

	private shouldShowUnreadBadgeInHeader = (conversation: ConversationSummary): boolean => {
		// Per ora mostra sempre il badge se ci sono messaggi non letti
		// TODO: Implementare logica più sofisticata quando ConversationSummary includerà info sul mittente
		return conversation.unreadCount > 0;
	};

	private getFilteredUnreadCount = (): number => {
		if (!this.state.conversationsSummary) {
			return 0;
		}
		
		return this.state.conversationsSummary
			.filter(conv => this.shouldShowUnreadBadgeInHeader(conv))
			.reduce((total, conv) => total + (conv.unreadCount || 0), 0);
	};

	private toggleMessageMenu = () => {
		this.setState((prev) => ({
			showMessageMenu: !prev.showMessageMenu
		}));
		// Non è più necessario caricare le conversazioni qui
		// perché vengono aggiornate automaticamente dal polling
	}

	private openConversation = (conversationId: string): void => {
		this.setState({ showMessageMenu: false });
		// Naviga alla pagina dei messaggi con la conversazione selezionata
		this.props.navigate(`/messages?conversation=${conversationId}`);
		
		// Aggiorna immediatamente i dati prima di navigare
		this.updateMessageData();
	}

	// Metodi per la gestione del polling dei messaggi
	private startMessagePolling = (): void => {
		// Evita di creare polling multipli
		if (this.messagePollingInterval) {
			return;
		}

		this.messagePollingInterval = setInterval(() => {
			this.updateMessageData();
		}, this.POLLING_INTERVAL_MS);
	}

	private stopMessagePolling = (): void => {
		if (this.messagePollingInterval) {
			clearInterval(this.messagePollingInterval);
			this.messagePollingInterval = null;
		}
	}

	// Metodo pubblico per fermare temporaneamente il polling (utile quando si è nella pagina messaggi)
	public pauseMessagePolling = (): void => {
		this.stopMessagePolling();
	}

	// Metodo pubblico per riavviare il polling
	public resumeMessagePolling = (): void => {
		this.startMessagePolling();
	}

	private updateMessageData = (): void => {
		// Evita richieste multiple simultanee
		if (this.state.isUpdatingMessages) {
			return;
		}

		this.setState({ isUpdatingMessages: true });

		// Aggiorna il conteggio dei messaggi non letti
		this.getUnreadMessagesCount();
		
		// Aggiorna anche le conversazioni per il popup
		// Questo assicura che il badge sia sempre aggiornato
		this.getConversationsSummary();
	}


	render() {
		const profile = StorageEntries.readProfile()
		const notifications = this.state.notificationActions?.items ?? []
		const unreadNotifications = notifications.filter((notification) => !this.isNotificationRead(notification))
		const unreadProposalNotifications = unreadNotifications
			.filter((notification) => this.getNotificationType(notification) === "PROPOSAL")
			.sort((a, b) => this.getNotificationTimestamp(b) - this.getNotificationTimestamp(a))
		const unreadOtherNotifications = unreadNotifications
			.filter((notification) => this.getNotificationType(notification) !== "PROPOSAL")
			.sort((a, b) => this.getNotificationTimestamp(b) - this.getNotificationTimestamp(a))
		const readNotifications = notifications
			.filter((notification) => this.isNotificationRead(notification))
			.sort((a, b) => this.getNotificationTimestamp(b) - this.getNotificationTimestamp(a))
			.slice(0, 5)
		const hasNotifications = notifications.length > 0
		const unreadNotificationsCount = unreadNotifications.length
		const unreadProposalNotificationItems = unreadProposalNotifications.map(this.buildNotificationItemData)
		const unreadOtherNotificationItems = unreadOtherNotifications.map(this.buildNotificationItemData)
		const readNotificationItems = readNotifications.map(this.buildNotificationItemData)
		const notificationsTotal = this.state.notificationActions?.pagination.totalRecords || 0
		const selectedActionAssetId = this.getSelectedActionAssetId()
		const selectedActionMatchId = this.getSelectedActionMatchId()
		const selectedActionType = this.state.selectedAction ? this.getNotificationType(this.state.selectedAction) : null
		const isProposalOrMatching = selectedActionType === "PROPOSAL" || selectedActionType === "MATCHING"
		const isAdminUser = this.props.sessionData.accountType === "ADMIN"
		const isBrandUser = this.props.sessionData.accountType === "BRAND"

		return (
			<>
				<WelcomeModal
					open={this.state.showWelcomeModal && (unreadNotificationsCount > 0 || this.state.unreadMessagesCount > 0)}
					onClose={this.closeWelcomeModal}
					unreadNotificationsCount={unreadNotificationsCount}
					unreadMessagesCount={this.state.unreadMessagesCount}
				/>

				<Dialog
					open={this.state.showActionDialog}
					onClose={this.closeActionDialog}
					fullWidth
					maxWidth="sm"
				>
					<DialogTitle>
						{this.state.selectedAction?.name || "Dettaglio Notifica"}
					</DialogTitle>
						<DialogContent dividers>
						{this.state.selectedAction && (
							<Box
								sx={{
									minHeight: 100,
									border: "1px solid lightgray",
									borderRadius: 1,
									p: 1.5,
								}}
							>
								<Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
									{this.getSelectedActionContentText() || "-"}
								</Typography>
							</Box>
						)}
						{(selectedActionAssetId || (isProposalOrMatching && selectedActionMatchId && (isAdminUser || isBrandUser))) && (
							<Stack direction="column" spacing={1.5} sx={{ mt: 2, alignItems: "center" }}>
								{isProposalOrMatching && selectedActionMatchId && isAdminUser && (
									<Button
										component={Link}
										to={`/match-proposal/match/${selectedActionMatchId}`}
										variant="contained"
										color="secondary"
										onClick={this.closeActionDialog}
									>
										Vai alla proposta
									</Button>
								)}
								{isProposalOrMatching && selectedActionMatchId && isBrandUser && (
									<Button
										component={Link}
										to={`/match/${selectedActionMatchId}`}
										variant="contained"
										color="secondary"
										onClick={this.closeActionDialog}
									>
										Vai al match
									</Button>
								)}
								{selectedActionAssetId && (
									<Button
										component={Link}
										to={`/asset/${selectedActionAssetId}`}
										variant="contained"
										color="secondary"
										onClick={this.closeActionDialog}
									>
										Vedi immobile
									</Button>
								)}
							</Stack>
						)}
					</DialogContent>
					<DialogActions>
						<Button onClick={this.closeActionDialog} color="primary">
							Chiudi
						</Button>
					</DialogActions>
				</Dialog>
				<Box>
					<CssBaseline />

					<AppBar
						position="fixed"
						sx={{
							width: { xl: `calc(100% - ${this.drawerWidth}px)` },
							ml: { xl: `${this.drawerWidth}px` },
							backgroundColor: "background.default",
						}}
						elevation={0}
					>
						<Toolbar sx={{ mr: 6 }}>
							<div>
								<IconButton
									aria-label="open drawer"
									edge="start"
									onClick={this.handleDrawerToggle}
									sx={{ mr: 2, display: { xl: 'none' }, color: "text" }}
								>
									<Icon path={mdiMenu} size={1.25} />
								</IconButton>
							</div>
							<Typography sx={{ flexGrow: 2 }} />
							<Stack direction="row" spacing={4}>
								{/* Icona messaggi - visibile per tutti gli utenti */}
								<Badge
									badgeContent={this.getFilteredUnreadCount()}
									color="error"
									overlap="circular"
									anchorOrigin={{
										vertical: 'top',
										horizontal: 'right'
									}}>
									<CustomIconButton onClick={this.toggleMessageMenu} ref={this.messageMenuRef}>
										<Message sx={{ fontSize: '1.5rem' }} />
									</CustomIconButton>
								</Badge>

								{profile === "BRAND" &&
									<>
										<Divider orientation="vertical" flexItem sx={{ borderColor: "#ADB5BD" }} />
									</>
								}
								<Menu
									id="menu-appbar"
									anchorEl={this.settingsMenuRef.current}
									anchorOrigin={{
										vertical: 'bottom',
										horizontal: 'right',
									}}
									keepMounted
									transformOrigin={{
										vertical: 'top',
										horizontal: 'right',
									}}
									open={this.state.showSettingsMenu}
									onClose={this.closeSettingsMenu}
									disableScrollLock
								>
									<CustomMenuItem onClick={this.closeSettingsMenu}>
										<CustomListItemIcon>
											<Icon path={mdiAccountMultiple} size={0.75} />
										</CustomListItemIcon>
										<CustomListItemText>Gestione utenti</CustomListItemText>
									</CustomMenuItem>
									<Divider variant="middle" sx={{ my: 0.5 }} />
									<CustomMenuItem onClick={this.closeSettingsMenu}>
										<CustomListItemIcon>
											<Icon path={mdiHeartPlusOutline} size={0.75} />
										</CustomListItemIcon>
										<CustomListItemText>Gestione preferiti</CustomListItemText>
									</CustomMenuItem>
									<Divider variant="middle" sx={{ my: 0.5 }} />
									<CustomMenuItem onClick={this.closeSettingsMenu}>
										<CustomListItemIcon>
											<Icon path={mdiCalendarMonth} size={0.75} />
										</CustomListItemIcon>
										<CustomListItemText>Calendario</CustomListItemText>
									</CustomMenuItem>
									<Divider variant="middle" sx={{ my: 0.5 }} />
									<CustomMenuItem onClick={this.closeSettingsMenu}>
										<CustomListItemIcon>
											<Icon path={mdiAccountTieHat} size={0.75} />
										</CustomListItemIcon>
										<CustomListItemText>Gestione permessi</CustomListItemText>
									</CustomMenuItem>
								</Menu>

								<Badge
									badgeContent={unreadNotifications.length}
									color="error"
									overlap="circular"
									anchorOrigin={{
										vertical: 'top',
										horizontal: 'right'
									}}>
									<CustomIconButton onClick={this.toggleNotificationMenu} ref={this.notificationMenuRef}>
										<Notifications sx={{ fontSize: '1.5rem' }} />
									</CustomIconButton>
								</Badge>
								<CustomIconButton onClick={this.openUserMenu} ref={this.userMenuRef}>
									<AccountCircle sx={{ fontSize: '1.5rem' }} />
								</CustomIconButton>
								<Menu
									id="menu-appbar"
									anchorEl={this.userMenuRef.current}
									anchorOrigin={{
										vertical: 'bottom',
										horizontal: 'right',
									}}
									keepMounted
									transformOrigin={{
										vertical: 'top',
										horizontal: 'right',
									}}
									open={this.state.showUserMenu}
									onClose={this.closeUserMenu}
									disableScrollLock
								>
									<CustomMenuItem onClick={this.openProfileDetail}>
										<CustomListItemText sx={{ textAlign: "right", }}>Profilo</CustomListItemText>
									</CustomMenuItem>
									<Divider variant="middle" sx={{ my: 0.5 }} />
									<CustomMenuItem onClick={this.logout}>
										<CustomListItemText sx={{ textAlign: "right", }}>Esci</CustomListItemText>
									</CustomMenuItem>
								</Menu>
								<NotificationMenu
									anchorEl={this.notificationMenuRef.current}
									open={this.state.showNotificationMenu}
									onClose={this.toggleNotificationMenu}
									notificationsTotal={notificationsTotal}
									hasNotifications={hasNotifications}
									unreadProposalNotifications={unreadProposalNotificationItems}
									unreadOtherNotifications={unreadOtherNotificationItems}
									readNotifications={readNotificationItems}
									onOpenDetails={this.openDetails}
									listRef={this.notificationListRef}
								/>
								
								{/* Menu popup messaggi */}
								<Menu
									id="message-appbar"
									anchorEl={this.messageMenuRef.current}
									anchorOrigin={{
										vertical: 'bottom',
										horizontal: 'right',
									}}
									keepMounted
									transformOrigin={{
										vertical: 'top',
										horizontal: 'right',
									}}
									open={this.state.showMessageMenu}
									onClose={this.toggleMessageMenu}
									disableScrollLock
									MenuListProps={{ sx: { padding: 0 } }}
									PaperProps={{
										sx: {
											mt: 1,
											boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
											borderRadius: 2,
										}
									}}
								>
									<NotificationContainer>
										<NotificationHeader>
											Messaggi ({this.getFilteredUnreadCount()})
										</NotificationHeader>
										
										{this.state.conversationsSummary && this.state.conversationsSummary.length > 0 ? (
											<NotificationList ref={this.messageListRef}>
												{this.state.conversationsSummary.map((conversation: ConversationSummary, index: number) => (
													<ListItemButton
														key={conversation.id}
														onClick={() => this.openConversation(conversation.id)}
														sx={{ px: 2, py: 1.5 }}
													>
														<ListItemText sx={{ margin: 0 }}>
															<Box>
																<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
																	{typeof conversation.subject === 'string' ? conversation.subject : 'Conversazione senza titolo'}
																</Typography>
																<Typography variant="body2" color="text.secondary" sx={{
																	overflow: 'hidden',
																	textOverflow: 'ellipsis',
																	whiteSpace: 'nowrap',
																	mb: 0.5
																}}>
																	{typeof conversation.lastMessage === 'string' ? conversation.lastMessage : 'Nessun messaggio'}
																</Typography>
																<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
																	<Typography variant="caption" color="text.secondary">
																		{(() => {
																			if (!conversation.participants || !Array.isArray(conversation.participants)) {
																				return 'Nessun partecipante';
																			}
																			const validParticipants = conversation.participants.filter(p => typeof p === 'string' && p.trim() !== '');
																			return validParticipants.length > 0 ? validParticipants.join(', ') : 'Nessun partecipante';
																		})()}
																	</Typography>
																	{this.shouldShowUnreadBadgeInHeader(conversation) && (
																		<Badge badgeContent={conversation.unreadCount} color="error" />
																	)}
																</Box>
															</Box>
														</ListItemText>
													</ListItemButton>
												))}
											</NotificationList>
										) : (
											<EmptyNotificationContainer>
												<Message sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
												<Typography variant="body2" textAlign="center">
													<Trans id="messages.no-messages">
														Nessun messaggio trovato
													</Trans>
												</Typography>
											</EmptyNotificationContainer>
										)}
										
										{/* Tasto "Vedi tutti i messaggi" sempre visibile */}
										<ViewAllButtonContainer>
											<Button
												component={Link}
												to="/messages"
												variant="contained"
												color="secondary"
												disableElevation
												onClick={this.toggleMessageMenu}
												sx={{
													width: "100%",
													textTransform: "none",
													fontSize: "0.9rem",
													fontWeight: 500,
													py: 1,
												}}
											>
												Vedi tutti i messaggi
											</Button>
										</ViewAllButtonContainer>
									</NotificationContainer>
								</Menu>
							</Stack>
						</Toolbar>
					</AppBar>

					<Sidebar ref={this.sidebarRef} width={this.drawerWidth} sessionData={this.props.sessionData} />

					<Popover
						anchorEl={this.userMenuRef.current}
						open={this.state.profileDetailOpen}
						onClose={this.closeProfileDetail}
						anchorOrigin={{
							vertical: "top",
							horizontal: "right",
						}}
						slotProps={{
							paper: {
								sx: {
									width: "310px",
									height: "100vh",
									overflowY: "scroll",
								},
							},
						}}
					>
						<Box sx={{ textAlign: "right" }}>
							<IconButton disableRipple={true} onClick={this.closeProfileDetail}>
								<Icon
									path={mdiCloseThick}
									size={0.5}
									color="white"
									style={{
										backgroundColor: "black",
										borderRadius: "10%",
										marginRight: 5,
										textAlign: "right",
									}}
								/>
							</IconButton>
						</Box>

						<Box sx={{
							px: 3,
							py: 1,
						}}>
							<Typography sx={{ textAlign: "center", pt: 1, }}>Profilo</Typography>

							<Box sx={{ pt: 3, textAlign: "center" }}>
								<Badge
									overlap="circular"
									anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
									badgeContent={
										<Icon
											path={mdiPencilOutline}
											size={1.2} color={"white"}
											style={{
												backgroundColor: theme.palette.secondary.main,
												borderRadius: '50%',
												border: '2px solid white',
												padding: '10%'
											}}
										/>
									}
								>
									<Avatar sx={{
										width: 65,
										height: 65,
										margin: "0 auto",
									}}
										src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Kubrick_on_the_set_of_Barry_Lyndon_%281975_publicity_photo%29_crop.jpg" />
								</Badge>
								<Typography fontSize={8} mt={1}>Hai effettuato l'accesso come</Typography>
								{this.props.sessionData.accountType === "ADMIN" &&
									<Typography fontSize={13} mt={0.5}>Super Admin</Typography>
								}
								{this.props.sessionData.accountType === "BRAND" &&
									<Typography fontSize={13} mt={0.5}>Utente Brand</Typography>
								}
								<Button
									type="button"
									size="small"
									variant="contained"
									disableElevation
									color="secondary"
									sx={{
										textTransform: 'none',
										color: "white",
										fontSize: 10,
										paddingY: 1,
										paddingX: 3,
										mt: 1,
									}}>
									Modifica password
								</Button>
							</Box>

							<Typography sx={{ pt: 3, pb: 2 }}>Info</Typography>

							{
								this.state.profileDetails &&
								<SidebarProfileInfoForm profileDetails={this.state.profileDetails} submit={this.submitForm} />
							}

						</Box>
					</Popover>
				</Box>
			</>
		)
	}
}

export default withUseRouter(Header)
